import type { Identifier } from "ra-core";
import type { PlanePushResult } from "../providers/supabase/dataProvider";
import type { Sale, ZoomMeetingSummary } from "../types";

// New tasks default to a due date 7 days out — used by the manual composer and
// the Zoom task-creation flow.
export const defaultDueDate = (): string =>
  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

// A task's category derives from its assignee's role at creation time (role
// tokens ARE category tokens: sales / follow-up / dev). Creation-time default
// only — reassignment or later edits never re-derive.
export const categoryForSale = (
  salesId: Identifier | null | undefined,
  sales: Pick<Sale, "id" | "role">[] | undefined,
  fallback = "sales",
): string => {
  if (salesId == null) return fallback;
  return sales?.find((s) => s.id === salesId)?.role ?? fallback;
};

// Build the `tasks` row payload from a Zoom summary draft. Shared by the per-row
// "Create task" and "Create all remaining" flows so both stay identical.
// A meeting task is always an internal teammate task: category derives from the
// assignee's role, drafts flagged is_external are never materialized (hidden by
// the Resolution Map), and the type/external concepts are retired at creation.
//
// contact_id carries through: on a draft it now means what it means on the row,
// "this task is about that person". LCATOMICZO-31. Note `?? null` does NOT catch
// an empty string — "" is not nullish, and PostgREST rejects it for a bigint.
// The picker's `parse` in TaskInputs is what keeps "" out of the draft.
export const buildTaskRow = (
  draft: ZoomMeetingSummary["tasks"][number],
  zoomMeetingId: Identifier,
  sales?: Pick<Sale, "id" | "role">[],
) => ({
  text: draft.text,
  due_date: draft.due_date ?? defaultDueDate(),
  type: "none",
  category: categoryForSale(draft.sales_id, sales, "follow-up"),
  sales_id: draft.sales_id,
  contact_id: draft.contact_id ?? null,
  // Link the task back to its source meeting (+ the moment it came up).
  zoom_meeting_id: zoomMeetingId,
  offset_seconds: draft.offset_seconds ?? null,
  collaboration_id: draft.collaboration_id ?? null,
});

// Distinct assignees a Plane push skipped because they aren't members yet,
// keeping name + email, deduped by email (falling back to name). Surfaced in a
// results dialog so they can be onboarded and the push re-run. Shared by the
// per-row, bulk-all, and bulk-selected push flows.
export const collectSkippedPlaneUsers = (
  results: PlanePushResult[],
): { name: string | null; email: string | null }[] => {
  const seen = new Set<string>();
  const users: { name: string | null; email: string | null }[] = [];
  for (const r of results) {
    if (r.ok || r.reason !== "assignee_not_member") continue;
    const a = r.assignee ?? { name: null, email: null };
    const key = (a.email || a.name || "unknown").toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    users.push(a);
  }
  return users;
};

// Date-only string N days from today — used by the reschedule presets/pill.
export const dueDateInDays = (days: number): string =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

// Split a "Names" string on the connectors Zoom uses between people.
const splitNames = (value: string): string[] =>
  value
    .split(/\s*(?:\/|&|,|\band\b)\s*/i)
    .map((name) => name.trim())
    .filter(Boolean);

// A Zoom-parsed action item can cover multiple people. Returns the assignee
// names so the caller can create one member task per person (a collaboration):
//   - slash-separated assignee ("John/Jane")  -> ["John", "Jane"]
//   - the "Collaboration" grouping label       -> names from the "A and B:" text prefix
//   - anything else                            -> [assignee]
// Falls back to the single assignee when no names can be extracted — the user
// confirms/edits in the Zoom review screen anyway.
export const resolveAssignees = (
  assigneeName: string | undefined,
  text: string,
): string[] => {
  const name = (assigneeName ?? "").trim();
  if (name.includes("/")) {
    const parts = splitNames(name);
    return parts.length ? parts : [name];
  }
  if (name.toLowerCase() === "collaboration") {
    const colon = text.indexOf(":");
    // Only treat a short "A and B:" prefix as names — not a whole sentence.
    if (colon > 0 && colon <= 60) {
      const parts = splitNames(text.slice(0, colon));
      if (parts.length) return parts;
    }
    return [name];
  }
  return name ? [name] : [];
};
